import React, { useEffect, useState, useRef } from 'react';
import { Table, Spin, message, Row, Col, Breadcrumb, Card, Button } from 'antd';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchConversionRateById } from '../../slices/conversion-rate';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { PrinterOutlined, FileExcelOutlined } from '@ant-design/icons';
import { CSVLink } from 'react-csv';
import logo from '../../Images/logo.svg';

const ViewConversionRate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [conversionData, setConversionData] = useState([]);
    const [loading, setLoading] = useState(false);
    const tableRef = useRef(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await dispatch(fetchConversionRateById(id)).unwrap();
            if (response.results && response.results.data) {
                setConversionData(response.results.data);
            } else {
                message.error('No conversion rate data found.');
            }
        } catch (err) {
            message.error('Failed to fetch conversion rate data.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [dispatch, id]);

    const handlePrint = () => {
        const printContent = tableRef.current.innerHTML;
        const currentDateTime = new Date().toLocaleString();

        const windowPrint = window.open('', '', 'width=1200,height=900');
        windowPrint.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Conversion Rate Report</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            font-size: 20px;
            line-height: 1.2;
            color: #000;
            margin: 20px;
            position: relative;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          table, th, td {
            border: 1px solid #333;
          }
          th, td {
            padding: 4px;
            text-align: center;
            font-size: 20px;
          }
          th {
            background: #eee;
          }
          @page {
            size: A4 landscape;
            margin: 20mm;
          }

          /* Logo - top-right (only first page) */
          .logo {
            position: fixed;
            top: 20px;
            right: 20px;
          }
          .logo img {
            height: 50px;
          }

          /* Footer */
          .footer {
            position: fixed;
            bottom: 10px;
            left: 20px;
            right: 20px;
            font-size: 14px;
            display: flex;
            justify-content: space-between;
          }

          /* Page number with CSS counter */
          @media print {
            body {
              counter-reset: page;
            }
            .pagenum:after {
              counter-increment: page;
              content: "Page " counter(page);
            }
          }
        </style>
      </head>
      <body>
        <!-- Logo (first page only) -->
        <div class="logo">
          <img src="${logo}" alt="Logo" />
        </div>

        <h2 style="text-align: center;">Conversion Rate Report</h2>
        <div>${printContent}</div>

        <!-- Footer -->
        <div class="footer">
          <span>${currentDateTime}</span>
          <span class="pagenum"></span>
        </div>
      </body>
    </html>
  `);

        windowPrint.document.close();
        windowPrint.focus();

        setTimeout(() => {
            windowPrint.print();
            windowPrint.close();
        }, 500);
    };


    const csvHeaders = [
        { label: 'Sr. No.', key: 'srNo' },
        { label: 'Profile', key: 'die' },
        { label: 'Alloy', key: 'alloy' },
        { label: 'Temper', key: 'temper' },
        { label: 'Conversion', key: 'conversion' },
        { label: 'Remarks', key: 'remarks' },
    ];

    const columns = [
        {
            title: 'Sr. No.',
            key: 'srNo',
            render: (text, record, index) => index + 1,
        },
        {
            title: 'Profile',
            dataIndex: 'die',
            key: 'die',
        },
        {
            title: 'Profile Image',
            dataIndex: 'die_diagram',
            key: 'die_diagram',
            render: (text, record) => (
                <img
                    src={record.die_diagram}
                    alt="Die Diagram"
                    style={{ width: '100px', height: 'auto' }}
                />
            ),
        },
        {
            title: 'Alloy',
            dataIndex: 'alloy',
            key: 'alloy',
        },
        {
            title: 'Temper',
            dataIndex: 'temper',
            key: 'temper',
        },
        {
            title: 'Conversion',
            dataIndex: 'conversion',
            key: 'conversion',
        },
        {
            title: 'Remarks',
            dataIndex: 'remarks',
            key: 'remarks',
            render: (text) => text || '-',
        },
    ];

    return (
        <div>
            <div className="sticyaction">
                <Row
                    className="pagenamerow mb-0"
                    justify="space-between"
                    align="middle"
                >
                    <Col>
                        <h2 style={{ margin: 0 }}>View Conversion Rate</h2>
                        <div className="bredcrumbwrp">
                            <Link onClick={() => navigate(-1)} className="back">
                                <Back /> BACK
                            </Link>
                            <Breadcrumb
                                items={[
                                    { title: <Link to="/dashboard">Home</Link> },
                                    { title: <Link to="/conversion-rate-list">Conversation Rate</Link> },
                                    { title: 'View Conversion Rate' },
                                ]}
                            />
                        </div>
                    </Col>
                </Row>
            </div>

            <div>
                <Card
                    title={
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <span>Conversion Rate Details</span>
                            <div>
                                <Button
                                    icon={<PrinterOutlined />}
                                    disabled={!conversionData.length}
                                    onClick={handlePrint}
                                    style={{ marginRight: '8px' }}
                                >
                                    Print
                                </Button>

                                <CSVLink
                                    data={conversionData.map((row, index) => ({
                                        srNo: index + 1,
                                        ...row,
                                    }))}
                                    headers={csvHeaders}
                                    filename={`conversion_rate_${id}.csv`}
                                    style={{ marginRight: '8px' }}
                                >
                                    <Button
                                        icon={<FileExcelOutlined />}
                                        disabled={!conversionData.length}
                                    >
                                        Excel
                                    </Button>
                                </CSVLink>

                                <Button
                                    type="primary"
                                    loading={loading}
                                    onClick={() => fetchData()}
                                >
                                    Refresh
                                </Button>
                            </div>
                        </div>
                    }
                    style={{ marginBottom: '20px' }}
                    headStyle={{ backgroundColor: '#f0f2f5', fontWeight: 'bold' }}
                >
                    <Spin spinning={loading}>
                        <div ref={tableRef}>
                            <Table
                                columns={columns}
                                dataSource={conversionData}
                                pagination={false}
                                bordered
                                style={{ marginTop: '16px' }}
                                rowKey="id"
                            />
                        </div>
                    </Spin>
                </Card>
            </div>
        </div>
    );
};

export default ViewConversionRate;
